/**
 * InsuranceArea — "is the car covered, who paid for the cover, did we finance it?"
 *
 * ONE DESCENDING CHAIN: the cover, then the money for the cover, then the financing of that money.
 * That order is not a preference — it is the dependency. A premium cannot be recorded without a
 * policy, a loan cannot be written against a premium nobody paid, and an agreement cannot paper a
 * loan that does not exist. The screen this replaces listed all five as peers with equal red
 * crosses.
 *
 * COMPLETION IS THE POLICY ALONE, AND THAT IS A BUG FIX. The old rule demanded policy AND loan AND
 * loan-instalment AND premium AND discount before this area could be called done — meaning a
 * contract with no insurance loan (which is most of them) could never complete this area.
 * `sign_contract` reads exactly one thing here: whether an `insurance_contracts` junction row
 * exists. The hire purchase agreement's eighteen merge fields contain ZERO insurance fields — the
 * restructured schedule kept `insurance.period_*` off this instrument deliberately, so that sending
 * a hire purchase agreement never waits on a policy being recorded. So the junction is the gate and
 * the other four are advisory records with attention states.
 *
 * ONE OF THOSE FOUR IS NOT ADVISORY ONCE ITS SUBJECT EXISTS, AND HAS NOT BEEN SINCE [D-B14] — the
 * paragraph above is about which rows this AREA is graded on, and the loan's paper is graded by
 * `sign_contract` instead: any contract carrying an `insurance_loans` row is refused until the
 * document over it is sealed. That is why `b-insloanagr` wears `Required` the moment a loan exists.
 *
 * AND THAT DOCUMENT NOW HAS TWO NAMES [D-B45]. `INSURANCE_LOAN` publishes an Insurance Premium
 * Advance for a year Primelane financed, and an Insurance Premium Payment Record for a year the
 * driver paid in full — the same subject, the same arc, the same activation rule, a different page.
 * The canvas READS which of the two a year is on (deposits covering the amount, and no instalment
 * row) and never writes it: the deposit that decides it is linked from the policy dossier's Advance
 * canvas or from the Insurance tab's payment sheet, and a third door onto one link row is not worth
 * the confusion.
 *
 * FIVE SHEETS DIED HERE — policy details, loan, instalment, premium, discount — and with them the
 * `TransactionSelector` sheet-in-a-sheet the last two opened. Every one of them was two or three
 * fields; every one is now the rows it always was. The picker over reusable policies replaces
 * `InsuranceSelector`, whose affordances were a hover-revealed pencil on a tile.
 *
 * WHAT COMMITS AND WHAT ACTS. A value that will be printed or charged is COMMITTED — the block's
 * own bar is the only thing that writes it. Pointing this contract at a policy that already exists
 * is an ACT: one junction row, undone in the same row that made it, and the destructive half of it
 * (which deletes the loan data recorded against the old policy) keeps the AlertDialog it has
 * always had. Ceremonies stay modal.
 *
 * THE PREMIUM ROW NAMES ITS OWN CONSEQUENCE. `count_transactions_by_purpose` only stops counting a
 * transaction once something links it, so an unrecorded premium leaves its bank transaction in the
 * Unassigned queue forever and nobody ever goes back for it. That sentence is on the row, because
 * "advisory" without a cost is just a row people learn to skip.
 *
 * RENEWAL IS NOT PART OF SETUP, BUT THE CANVAS IS EDITABLE ON EVERY CONTRACT REGARDLESS OF CHAIN
 * LENGTH (owner ruling, 2026-08-05). Setting a contract up is about the cover it was ACTIVATED
 * under, so the read binds to the first junction the contract recorded (`data/insurance.ts`) and
 * the policy drawn here is the original one. What that read does NOT do is lock: a contract that
 * has renewed is edited here exactly like one that has not. Chain length is DATA — the read-only
 * gate once hung off it was rejected — and nothing on this canvas refuses a write, or explains
 * itself away, because a contract carries more than one junction.
 *
 * WHAT THE THREE POLICY ACTS DO ON A CHAINED CONTRACT, SAID PLAINLY. Because the read is the
 * SETUP-ERA junction, that is what they reach. Switch, Unlink and Delete all act on the year the
 * contract was ACTIVATED under rather than the cover it is running today, and
 * `unlink_insurance_from_contract` CASCADES over the loans, instalments, repayments, deposits,
 * agreements and transactions recorded against that junction. THE CONFIRM DIALOGS ARE THE GUARD —
 * the same AlertDialogs that have always stood in front of those two acts, and now the only
 * friction on this canvas.
 *
 * THE AGREEMENT ARC SITS ON THE SAME JUNCTION [D-B14]. `AgreementSlotList` papers the SETUP
 * junction's loan and nothing else — its slots are minted from the same `earliestJunction` read
 * (`useContractAgreements`) — so on a chained contract the board is showing the ORIGINAL year's
 * loan, and [D-B14] makes that document required to activate.
 *
 * AND IT WAITS ON THE HIRE PURCHASE AGREEMENT, DRAWN LOCKED (owner's ruling, 2026-08-18). The
 * advance is made in connection with the master instrument and quotes its reference, so until the
 * driver has signed there is nothing to send: the block prints the condition in words, exactly as
 * the witness agreement's does on the agreements canvas, in place of offering a send that came back
 * as a red merge-field refusal naming a field nobody can type. The rule itself is
 * `boardSlots.advanceLockedFor` — one derivation, read by this block, the row and the manifest.
 *
 * THE ONE THING THAT STILL CLOSES IS THE LOAD, and it is invisible — see `writesClosed` below.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { businessDay, formatBusinessDate, formatCurrency } from '@/lib/format';
import { instrumentFor } from '@/lib/agreements/instrument';
import { type Attestation } from '@/lib/certificatePrefill';

import AgreementSlotList, {
    type ContractAgreementsModel,
} from '@/components/agreements/AgreementSlotList';
import { advanceLockedFor, type BoardSlot } from '@/components/agreements/boardSlots';
import { AGREEMENT_BOARD_SUBJECTS_KEY } from '@/components/agreements/useContractAgreements';
import { DEPOSIT_LEDGER_KEY } from '@/components/insurance-sub/depositLedger';
import { PREMIUM_YEARS_KEY } from '@/components/insurance-sub/premiumLedger';

import {
    CONTRACT_INSURANCE_KEY,
    fetchReusableInsurances,
    linkInsurancePolicy,
    reusableInsurancesKey,
    saveDiscountLink,
    saveInsuranceLoan,
    saveLoanInstalment,
    savePolicyDetails,
    savePremiumLink,
    useContractInsurance,
} from '../data/insurance';
import {
    policyLabel,
    policyMeta,
    policyPeriodFlag,
    policyPeriodText,
    policySearchText,
    withCurrentPolicy,
    type PickablePolicy,
} from '../data/policyDraft';
import {
    fieldErrorsFrom,
    INSURANCE_LOAN_REFUSALS,
    POLICY_REFUSAL_PREFIXES,
    POLICY_REFUSALS,
    PREMIUM_REFUSALS,
} from '../data/refusalFields';
import { UNASSIGNED_TRANSACTIONS_KEY } from '../data/transactions';
import { useCertificateRow, type CertificateCommit } from '../inline/CertificateRow';
import InlineFieldGroup from '../inline/InlineFieldGroup';
import InlinePickerPopover, { type PickerRow } from '../inline/InlinePickerPopover';
import QuietField from '../inline/QuietField';
import TransactionPicker from '../inline/TransactionPicker';
import type { FieldValues } from '../inline/inlineEditReducer';
import { useInlineGroup } from '../inline/useInlineGroup';
import { useStagedFile } from '../inline/useStagedFile';
import type { SetupArea } from '../steps';
import { Block, BlockActions, Blocks, LockLine, Rec, Rows } from './blocks';

interface Props {
    contractId: number;
    vehicleId: number;
    /**
     * WHAT THE CERTIFICATE HAS TO NAME — `vehicle_registrations.number` and `vehicles.vin` for the
     * car this contract is written over. Prop-drilled from `ContractSetup`, which already holds
     * both in the setup envelope (`data/contract.ts` selects `vin` and the registration embed), so
     * this costs no read. `vehicleId` alone could not answer it: the registration helper on this
     * canvas returns the registration row and never the chassis.
     */
    vehiclePlate: string | null;
    vehicleVin: string | null;
    /** The INSURANCE_LOAN slots, from `partitionSlotsByStep(agreements.slots).insurance`. */
    slots: BoardSlot[];
    agreements: ContractAgreementsModel;
    onGoToArea: (area: SetupArea) => void;
    onSuccess: () => void;
}

/**
 * `yyyy-MM-dd`, which is what a native date input holds and what a `date` column takes.
 *
 * THROUGH `businessDay`, BECAUSE `new Date('2026-04-17')` IS AN INSTANT AND NOT A DAY. Parsed as
 * UTC midnight and then formatted in the viewer's zone, it prints April 16th anywhere west of
 * Greenwich — so the input opened a day early on the value the server holds, and re-saving the
 * block persisted the shift. `businessDay` lands a date string on the Ghana calendar day it names.
 */
function dateValue(value: string | null | undefined): string {
    const day = businessDay(value);
    return day ? format(day, 'yyyy-MM-dd') : '';
}

/**
 * WHAT A WRITE THAT BEAT THE ENVELOPE HOME IS TOLD — the only refusal this canvas raises on its
 * own. It is transient and says so, and it is deliberately printed on no block: nothing is wrong,
 * and a sentence that outlives the few hundred milliseconds it describes is just noise.
 */
const READ_IN_FLIGHT_REFUSAL =
    'The insurance record for this contract is still loading. Try that again in a moment.';

export default function InsuranceArea({
    contractId,
    vehicleId,
    vehiclePlate,
    vehicleVin,
    slots,
    agreements,
    onGoToArea,
    onSuccess,
}: Props) {
    const queryClient = useQueryClient();

    const [unlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false);
    const [pendingSwapId, setPendingSwapId] = useState<number | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    /**
     * Whichever of the four policy acts was refused, in the server's own words.
     *
     * NOT A TOAST. `unlink_insurance_from_contract` and `delete_insurance` refuse for reasons an
     * admin has to act on, and a toast carrying one of those sentences is gone before the block it
     * was about can be looked at. It is read inside the ceremony that asked while one is open, and
     * under the policy row otherwise — the same routing every other refusal on this canvas takes.
     */
    const [refusal, setRefusal] = useState<string | null>(null);
    const [policyOpen, setPolicyOpen] = useState(false);
    const [loanOpen, setLoanOpen] = useState(false);
    /** How many policy commits have LANDED in this session — the certificate row's own stamp. */
    const [policyCommits, setPolicyCommits] = useState(0);
    /**
     * WHAT THE ADMIN CONFIRMED ABOUT A SCAN NOTHING HERE CAN READ [D-B56], HELD BY THE CANVAS
     * BECAUSE IT TRAVELS WITH THE SAVE. The row raises it, re-asks every render whether it still
     * holds, and drops it on any edit; what this block does with it is put it in `savePolicyDetails`.
     */
    const [attested, setAttested] = useState<Attestation | null>(null);

    // THE ONE INSURANCE READ. The envelope, never a second flattening of it.
    // `isFetching` AS WELL AS `isLoading`, AND THE TWO ARE NOT THE SAME WINDOW. `isLoading` is the
    // FIRST load only; every refetch after a link, swap, unlink or delete leaves it false while
    // react-query serves the PREVIOUS policy's data for the key. The publish effect below is the
    // one thing on this area that must see that window — see its own docblock.
    const {
        data: envelope,
        isError,
        isLoading: insuranceLoading,
        isFetching: insuranceFetching,
    } = useContractInsurance(contractId);
    const insurance = envelope?.policy ?? null;
    /** `insurance_loans` hang off the JUNCTION row, not off the policy. */
    const loans = envelope?.loans ?? [];
    const loan = loans[0] ?? null;
    const loanInstalment = loan?.insurance_loan_instalments?.[0] ?? null;

    /**
     * WHETHER THIS CONTRACT'S HIRE PURCHASE AGREEMENT WAS SIGNED ON PAPER [D-B57] — read off the
     * board's own conjunction, which it hangs on every slot.
     *
     * `failed` IS ASKED FIRST, AND IT HAS TO BE. `buildBoardSlots` takes the documents read as
     * `documents ?? []`, so a board whose documents came back refused still mints slots — and
     * `masterOnPaper`'s second limb, "no live hire purchase document", is then true of every
     * contract carrying a signed scan. Asked the other way round, a failed read would put a
     * settlement arrangement on the summary line of contracts that have no such arrangement.
     *
     * WHAT IT IS FOR IS ONE FRAGMENT OF THAT LINE. This canvas's loan save is one of the two routes
     * D-B57 names and leaves ungated; what changes here is only what the closed block says about a
     * premium that carries no weekly recovery.
     */
    const hpOnPaper = !agreements.failed && agreements.slots.some((slot) => slot.masterOnPaper);

    /**
     * WHAT THE DRIVER HAS PAID AGAINST THIS PREMIUM, and which instrument that makes the year
     * [D-B45].
     *
     * READ-ONLY ON THIS CANVAS, AND DELIBERATELY SO. `insurance_loan_deposits` is written from the
     * policy dossier's Advance canvas and from the Insurance tab's payment sheet; a third writer
     * here would be a third place the same link row is created from. What the canvas gains is SIGHT
     * of it —
     * without which the block goes on calling a premium the driver settled in full an advance, and
     * offers an interest rate over it.
     *
     * THE MONEY IS ON THE TRANSACTION. `insurance_loan_deposits` carries `loan_id` and
     * `transaction_id` and nothing else, so a deposit whose transaction the embed did not return
     * contributes nothing — which keeps a partial read naming the advance.
     */
    const deposits = loan?.insurance_loan_deposits ?? [];
    const depositTotal = deposits.reduce(
        (total, row) => total + (row.transactions?.amount ?? 0),
        0,
    );
    const depositPaidOn = deposits.reduce<string | null>((latest, row) => {
        const paidAt = row.transactions?.transaction_at ?? null;
        if (!paidAt) return latest;
        return latest === null || Date.parse(paidAt) >= Date.parse(latest) ? paidAt : latest;
    }, null);
    const instrument = instrumentFor({
        amount: loan?.amount ?? null,
        depositTotal: loan ? depositTotal : null,
        instalmentCount: loan ? (loan.insurance_loan_instalments ?? []).length : null,
    });
    const paidInFull = instrument === 'PAYMENT_RECORD';

    /**
     * THE WHOLE GATE: WRITES FAIL CLOSED WHILE THE ENVELOPE IS IN FLIGHT, AND NOTHING ELSE CLOSES.
     *
     * `undefined` is not a record. Until the round trip lands there is no junction id, no policy id
     * and no premium row to read, so a write taken in that window is a write from a page that does
     * not yet know what it is describing. The policy picker is the genuinely reachable one — its
     * options come from `get_reusable_insurances`, a separate and faster query — so on a deep link
     * into this area a policy could be linked, or the three policy fields typed and saved, before
     * the envelope had answered at all. Every `disabled` prop and every write guard below reads
     * this one boolean.
     *
     * IT IS INVISIBLE BY DESIGN. It holds for the few hundred milliseconds of the first load,
     * answers `READ_IN_FLIGHT_REFUSAL` if something beats it home, and is printed on no block.
     * A RENEWAL CHAIN DOES NOT CLOSE ANYTHING (owner ruling, 2026-08-05), and neither does a
     * contract on no cover at all — that one is how cover gets recorded in the first place.
     */
    const writesClosed = insuranceLoading;

    // THE JUNCTION ROWS ONLY. What the two payments SAY is read by the pickers that hold them —
    // the envelope's embedded `transactions` are no longer flattened here, because nothing on this
    // area prints them a second time.
    const premiumRecord = insurance?.insurance_premiums?.[0] ?? null;
    const discountRecord = premiumRecord?.insurance_premium_discounts?.[0] ?? null;

    const { data: reusable, isLoading: reusableLoading } = useQuery({
        queryKey: reusableInsurancesKey(vehicleId, contractId),
        queryFn: () => fetchReusableInsurances(vehicleId, contractId),
        enabled: !!vehicleId,
        staleTime: 30000,
    });

    // ── The policy's own values ──────────────────────────────────────────────
    const policyServer = useMemo<FieldValues>(
        () => ({
            valuedAt: insurance?.valued_at == null ? '' : String(insurance.valued_at),
            startsAt: dateValue(insurance?.starts_at),
            expiresAt: dateValue(insurance?.expires_at),
            certificate: '',
        }),
        [insurance?.valued_at, insurance?.starts_at, insurance?.expires_at],
    );

    const policy = useInlineGroup({
        /**
         * THE SUBJECT IS THIS CONTRACT'S COVER SLOT, NOT THE POLICY ROW — the same rule the
         * registration and roadworthiness blocks document. Filling these three fields on a
         * contract with no cover CREATES the policy, so keying the subject to `insurance.id` made
         * that first save look like a change of subject (`policy:new` → `policy:412`): the refetch
         * that followed reset the group and took its `Saved …` line away a second after it
         * appeared.
         *
         * RULE 5 STILL HAS ITS CASE, AND IT IS HANDLED WHERE IT HAPPENS. A draft typed against one
         * policy must not follow the picker onto another — so the link, swap, unlink and delete
         * acts discard this group and the loan group explicitly, which is the honest place for it:
         * the subject string was standing in for "somebody repointed the row", and those four
         * mutations ARE that event.
         */
        server: policyServer,
        subject: `policy:${contractId}`,
        // The prefix map carries the whole certificate family — every refusal `certificateRefusal`
        // can write, two of which name their own values — so each lands on the upload row rather
        // than on the group's own line.
        fieldErrorsFor: fieldErrorsFrom(POLICY_REFUSALS, POLICY_REFUSAL_PREFIXES),
        save: (draft) => {
            // THE GUARD IS ON THE WRITE, not only on the control. A disabled input is a courtesy;
            // a draft typed before the envelope landed can still reach Save without this.
            if (writesClosed) throw new Error(READ_IN_FLIGHT_REFUSAL);
            return savePolicyDetails({
                policyId: insurance?.id ?? null,
                contractId,
                vehicleId,
                vehicle: { plate: vehiclePlate, vin: vehicleVin },
                draft: {
                    valuedAt: draft.valuedAt,
                    startsAt: draft.startsAt,
                    expiresAt: draft.expiresAt,
                },
                certificateUrl: insurance?.certificate_url ?? null,
                certificateFile: certificate.file,
                // ASKED OF THE DRAFT BEING SAVED, NOT OF A RENDER-TIME FLAG — the row's own rule,
                // and the guard inside `savePolicyDetails` re-asks it of the bytes anyway.
                attestation: cert.attestationFor({
                    startsAt: draft.startsAt,
                    expiresAt: draft.expiresAt,
                }),
            });
        },
        onSaved: () => {
            // THE STAMP THE CERTIFICATE ROW READS, AND IT MOVES HERE BECAUSE HERE IS WHERE A COMMIT
            // LANDS. `savedAt` cannot be it — `useInlineGroup` stamps it `MMM d, yyyy · HH:mm`, so
            // two commits inside one clock minute are indistinguishable, and the row's held read
            // decides between applying and dropping itself on exactly that comparison.
            setPolicyCommits((commits) => commits + 1);
            queryClient.invalidateQueries({ queryKey: CONTRACT_INSURANCE_KEY });
            queryClient.invalidateQueries({ queryKey: ['prep-status'] });
            // The board's slots are scoped to THIS junction, and this save can create it.
            queryClient.invalidateQueries({ queryKey: AGREEMENT_BOARD_SUBJECTS_KEY });
            onSuccess();
        },
    });
    const certificate = useStagedFile(policy, 'certificate');

    /**
     * ── THE CERTIFICATE ROW, WHICH IS NO LONGER THIS FILE'S ──────────────────
     *
     * EIGHT HUNDRED LINES LEFT HERE AND BECAME `inline/CertificateRow.tsx`, unchanged in every
     * behaviour: the staged read, the read of the document already on file, the hold through a
     * commit, the attested filing of a scan, every sentence and every id. They left because the
     * insurance dossier's Policy canvas asks the same question of the same document, and a second
     * implementation of this row could not be made to agree with this one — `ocrOnNextFiledRead` is
     * a module-global one-shot, so two of them over one certificate would spend each other's pass.
     *
     * WHAT STAYS HERE IS WHAT BELONGS TO THIS GROUP. `useStagedFile` is the bridge to the group's
     * own draft marker — a file's `name:size` is the only reason the group goes dirty for a
     * document — so it stays, and the row is handed the `File` itself. The two date fields are
     * `policy.field(...)` spreads and stay in the markup below; the row only says what they point
     * at while the period warning stands. The attestation stays because it travels with the save.
     */
    const policyDates = useMemo(
        () => ({
            startsAt: policy.draft.startsAt ?? '',
            expiresAt: policy.draft.expiresAt ?? '',
        }),
        [policy.draft.startsAt, policy.draft.expiresAt],
    );
    /** The document already on this policy, and the standing of it [D-B56]. */
    const filedCertificate = useMemo(
        () => ({
            url: insurance?.certificate_url ?? null,
            attestedAt: insurance?.certificate_attested_at ?? null,
            attestedBy: insurance?.certificate_attested_by ?? null,
        }),
        [
            insurance?.certificate_url,
            insurance?.certificate_attested_at,
            insurance?.certificate_attested_by,
        ],
    );
    /**
     * HOW THIS GROUP'S LAST WRITE ENDED, IN THE THREE FACTS THE ROW ACTS ON.
     *
     * A held read is released onto a REFUSAL and dropped onto a landed commit, and those are the
     * reducer's `error` and `clean` endings — both reached only through `saving`, because rule 7
     * refuses an edit and a cancel while a write is in flight. `seq` is the count of commits that
     * have LANDED, which is what the row compares to notice one that landed underneath a read still
     * on its way: the group's own `savedAt` cannot be it, being a display string to the minute.
     */
    const policyCommit = useMemo<CertificateCommit>(
        () => ({
            saving: policy.saving,
            outcome:
                policy.state.status === 'error'
                    ? 'refused'
                    : policy.savedAt === null
                      ? 'idle'
                      : 'committed',
            seq: policyCommits,
        }),
        [policy.saving, policy.state.status, policy.savedAt, policyCommits],
    );

    const cert = useCertificateRow({
        dates: policyDates,
        onDateChange: policy.set,
        file: certificate.file,
        onFileChange: certificate.stage,
        attestation: attested,
        onAttestation: setAttested,
        vehicle: { plate: vehiclePlate, vin: vehicleVin },
        onFile: filedCertificate,
        policyId: insurance?.id ?? null,
        loading: writesClosed,
        refetching: insuranceFetching,
        commit: policyCommit,
        // What the attribution map routes onto the upload slot — the pattern `VehicleArea.tsx:777`
        // established. The row prints its own stage-time refusal first and this one after it.
        serverError: policy.field('certificate').error,
    });

    // ── The premium and its discount ─────────────────────────────────────────
    const premiumServer = useMemo<FieldValues>(
        () => ({
            premium: premiumRecord?.transaction_id == null ? '' : String(premiumRecord.transaction_id),
            discount:
                discountRecord?.transaction_id == null ? '' : String(discountRecord.transaction_id),
        }),
        [premiumRecord?.transaction_id, discountRecord?.transaction_id],
    );

    const premium = useInlineGroup({
        server: premiumServer,
        // The premium hangs off the POLICY, which is already its owner: this group creates the
        // premium row, never the policy, so the subject does not move under its own first save.
        subject: insurance ? `premium:${insurance.id}` : 'premium:none',
        fieldErrorsFor: fieldErrorsFrom(PREMIUM_REFUSALS),
        save: async (draft) => {
            if (writesClosed) throw new Error(READ_IN_FLIGHT_REFUSAL);
            if (!insurance) throw new Error('Link a policy before recording what was paid for it.');

            // THE PREMIUM FIRST, AND ITS ID CARRIED FORWARD. A discount hangs off the premium row,
            // so recording both in one commit needs the id of a row that may not have existed a
            // moment ago — which is why `savePremiumLink` hands it back.
            let premiumId = premiumRecord?.id ?? null;
            if (draft.premium !== premiumServer.premium) {
                premiumId = await savePremiumLink({
                    premiumId,
                    insuranceId: insurance.id,
                    transactionId: draft.premium === '' ? null : Number(draft.premium),
                });
            }
            if (draft.discount !== premiumServer.discount) {
                await saveDiscountLink({
                    discountId: discountRecord?.id ?? null,
                    premiumId,
                    transactionId: draft.discount === '' ? null : Number(draft.discount),
                });
            }
        },
        onSaved: () => {
            queryClient.invalidateQueries({ queryKey: ['insurance-premium'] });
            queryClient.invalidateQueries({ queryKey: CONTRACT_INSURANCE_KEY });
            // The whole point of recording it: the transaction leaves the Unassigned queue.
            queryClient.invalidateQueries({ queryKey: UNASSIGNED_TRANSACTIONS_KEY });
            onSuccess();
        },
    });

    // ── The loan and its recovery ────────────────────────────────────────────
    const loanServer = useMemo<FieldValues>(
        () => ({
            amount: loan?.amount == null ? '' : String(loan.amount),
            rate: loan?.interest_rate == null ? '' : String(loan.interest_rate),
            instalment: loanInstalment?.amount == null ? '' : String(loanInstalment.amount),
            instalmentStart: dateValue(loanInstalment?.starts_at),
        }),
        [loan?.amount, loan?.interest_rate, loanInstalment?.amount, loanInstalment?.starts_at],
    );

    const loanGroup = useInlineGroup({
        server: loanServer,
        /**
         * THE SUBJECT IS THE JUNCTION, WHICH IS WHAT AN `insurance_loans` ROW HANGS OFF. Keyed to
         * `loan.id` the first save — the one that CREATES the loan — read as a change of subject
         * and reset the group with its provenance line. Keyed here it does not move on creation,
         * and it still moves for the only thing that genuinely repoints these values: a swap of
         * policy, which is a new junction row.
         */
        subject: envelope ? `loan:junction:${envelope.junctionId}` : 'loan:none',
        fieldErrorsFor: fieldErrorsFrom(INSURANCE_LOAN_REFUSALS),
        save: async (draft) => {
            if (writesClosed) throw new Error(READ_IN_FLIGHT_REFUSAL);
            if (!envelope) throw new Error('Link a policy before financing its premium.');

            let loanId = loan?.id ?? null;
            if (draft.amount !== loanServer.amount || draft.rate !== loanServer.rate) {
                loanId = await saveInsuranceLoan({
                    loanId,
                    junctionId: envelope.junctionId,
                    amount: draft.amount,
                    interestRate: draft.rate,
                });
            }
            const instalmentMoved =
                draft.instalment !== loanServer.instalment ||
                draft.instalmentStart !== loanServer.instalmentStart;
            if (instalmentMoved) {
                if (loanId == null) throw new Error('Record the loan before its weekly recovery.');
                await saveLoanInstalment({
                    instalmentId: loanInstalment?.id ?? null,
                    loanId,
                    amount: draft.instalment,
                    startsAt: draft.instalmentStart,
                });
            }
        },
        onSaved: () => {
            queryClient.invalidateQueries({ queryKey: CONTRACT_INSURANCE_KEY });
            // One INSURANCE_LOAN slot is minted per loan on this junction, so recording the loan
            // or its recovery is what brings the agreement's row onto the board.
            queryClient.invalidateQueries({ queryKey: AGREEMENT_BOARD_SUBJECTS_KEY });
            onSuccess();
        },
    });

    // ── The link, the swap and the two destructive ceremonies ────────────────

    /**
     * DROPPING THE POLICY DRAFT — WHAT THE GROUP'S SUBJECT STRING USED TO DO, DONE WHERE IT
     * ACTUALLY HAPPENS, AND THE ONLY ROUTINE ON THIS AREA THAT DOES IT.
     *
     * Its values belong to whichever policy this contract is on, and all four acts below change
     * which one that is (or removes it) — so a draft typed against the previous policy is not this
     * group's to keep, and one Save would write an insured value onto somebody else's record. The
     * LOAN group needs nothing here: its subject is the junction, which every one of these four
     * acts replaces or removes, so rule 5 fires on it by itself.
     *
     * AND IT IS WHAT `Discard` AND `Esc` RUN TOO, which is why it is passed down as the group's own
     * `discard`. `InlineFieldGroup` wires `group.discard` straight into the commit bar and the
     * block's key handler, so a bare `policy.discard` there would put the two date fields back and
     * leave the read that wrote over them still in flight — landing its dates on the draft the
     * admin just cleared, a second after they cleared it. There is one clearing routine and every
     * door into it goes through here.
     *
     * THE CERTIFICATE ROW CLEARS ITSELF, AND THAT IS ALL `cert.reset()` IS: the typed plate, the
     * attestation, a read in flight, the on-file handle and the prefill that names it. Every caller
     * below changes WHAT THIS ROW HOLDS — the four acts change which policy it is about, a Discard
     * puts the fields back to the server's values — and a read started before that would otherwise
     * land on top of the result.
     */
    const discardPolicy = () => {
        policy.discard();
        cert.reset();
    };

    const linkPolicy = useMutation({
        // GUARDED THOUGH IT LOOKS HARMLESS. The picker only routes here when the canvas believes
        // this contract is on nothing — and mid-load it believes that of every contract, because
        // the envelope has not answered. Linking then would add a junction to a contract that
        // already had one, behind an `undefined` this component read as "not on cover".
        mutationFn: (insuranceId: number) => {
            if (writesClosed) throw new Error(READ_IN_FLIGHT_REFUSAL);
            return linkInsurancePolicy(contractId, insuranceId);
        },
        onSuccess: () => {
            toast.success('Insurance policy linked');
            setRefusal(null);
            discardPolicy();
            queryClient.invalidateQueries({ queryKey: CONTRACT_INSURANCE_KEY });
            queryClient.invalidateQueries({ queryKey: ['prep-status'] });
            queryClient.invalidateQueries({ queryKey: AGREEMENT_BOARD_SUBJECTS_KEY });
            onSuccess();
        },
        onError: (error: Error) => setRefusal(error.message || 'The link was refused.'),
    });

    const swapPolicy = useMutation({
        mutationFn: async (newInsuranceId: number) => {
            if (writesClosed) throw new Error(READ_IN_FLIGHT_REFUSAL);
            if (!insurance) throw new Error('No policy to unlink');
            const { error: unlinkError } = await supabase.rpc('unlink_insurance_from_contract', {
                p_insurance_id: insurance.id,
                p_contract_id: contractId,
            });
            if (unlinkError) throw unlinkError;
            await linkInsurancePolicy(contractId, newInsuranceId);
        },
        onSuccess: () => {
            toast.success('Insurance policy switched');
            setUnlinkConfirmOpen(false);
            setPendingSwapId(null);
            setRefusal(null);
            discardPolicy();
            queryClient.invalidateQueries({ queryKey: CONTRACT_INSURANCE_KEY });
            queryClient.invalidateQueries({ queryKey: ['prep-status'] });
            queryClient.invalidateQueries({ queryKey: ['reusable-insurances'] });
            queryClient.invalidateQueries({ queryKey: ['insurance-policy'] });
            queryClient.invalidateQueries({ queryKey: AGREEMENT_BOARD_SUBJECTS_KEY });
            // THE UNLINK CASCADES OVER THE LOANS AND THEIR DEPOSITS, so the two estate ledgers are
            // short exactly those rows until they are read again.
            queryClient.invalidateQueries({ queryKey: DEPOSIT_LEDGER_KEY });
            queryClient.invalidateQueries({ queryKey: PREMIUM_YEARS_KEY });
            onSuccess();
        },
        onError: (error: Error) => setRefusal(error.message || 'The switch was refused.'),
    });

    const unlinkPolicy = useMutation({
        mutationFn: async () => {
            if (writesClosed) throw new Error(READ_IN_FLIGHT_REFUSAL);
            if (!insurance) throw new Error('No policy to unlink');
            const { error } = await supabase.rpc('unlink_insurance_from_contract', {
                p_insurance_id: insurance.id,
                p_contract_id: contractId,
            });
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success('Insurance policy unlinked');
            setUnlinkConfirmOpen(false);
            setRefusal(null);
            discardPolicy();
            queryClient.invalidateQueries({ queryKey: CONTRACT_INSURANCE_KEY });
            queryClient.invalidateQueries({ queryKey: ['prep-status'] });
            queryClient.invalidateQueries({ queryKey: ['reusable-insurances'] });
            queryClient.invalidateQueries({ queryKey: AGREEMENT_BOARD_SUBJECTS_KEY });
            // Same cascade as the switch above: the loans go, and their deposits with them.
            queryClient.invalidateQueries({ queryKey: DEPOSIT_LEDGER_KEY });
            queryClient.invalidateQueries({ queryKey: PREMIUM_YEARS_KEY });
            onSuccess();
        },
        // THE CEREMONY STAYS OPEN ON A REFUSAL, so the server's sentence is read where the decision
        // was taken rather than behind a dialog that has already dismissed itself.
        onError: (error: Error) => setRefusal(error.message || 'The unlink was refused.'),
    });

    const deletePolicy = useMutation({
        mutationFn: async () => {
            if (writesClosed) throw new Error(READ_IN_FLIGHT_REFUSAL);
            if (!insurance) throw new Error('No policy to delete');
            const { error } = await supabase.rpc('delete_insurance', { p_insurance_id: insurance.id });
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success('Insurance policy deleted');
            setDeleteConfirmOpen(false);
            setRefusal(null);
            discardPolicy();
            queryClient.invalidateQueries({ queryKey: CONTRACT_INSURANCE_KEY });
            queryClient.invalidateQueries({ queryKey: ['prep-status'] });
            queryClient.invalidateQueries({ queryKey: ['reusable-insurances'] });
            queryClient.invalidateQueries({ queryKey: ['insurance-policy'] });
            queryClient.invalidateQueries({ queryKey: AGREEMENT_BOARD_SUBJECTS_KEY });
            // `delete_insurance` deletes the deposits, the instalments and the loans before the
            // policy itself, so both estate ledgers move.
            queryClient.invalidateQueries({ queryKey: DEPOSIT_LEDGER_KEY });
            queryClient.invalidateQueries({ queryKey: PREMIUM_YEARS_KEY });
            onSuccess();
        },
        onError: (error: Error) => setRefusal(error.message || 'The delete was refused.'),
    });

    if (isError) {
        return (
            <p className="sd-lede sd-warn">
                The insurance read failed. Nothing has been changed — reload the page to try again.
            </p>
        );
    }

    const period = policyPeriodText(insurance?.starts_at, insurance?.expires_at);
    // READ OFF THE SERVER ROW, NOT THE DRAFT: the collapsed head describes what is RECORDED, and a
    // head that changed as somebody typed would be describing a period nobody has saved.
    const periodFlag = policyPeriodFlag(insurance?.starts_at, insurance?.expires_at);

    const currentAsRow: PickablePolicy | null = insurance
        ? {
              id: insurance.id,
              valued_at: insurance.valued_at ?? null,
              starts_at: insurance.starts_at ?? null,
              expires_at: insurance.expires_at ?? null,
              certificate_url: insurance.certificate_url ?? null,
          }
        : null;

    // NO `onQueryChange`: `get_reusable_insurances` returns the whole list in one answer, so the
    // every-term-anywhere filter over what came back is the only filter there is to run.
    const policyRows: PickerRow<PickablePolicy>[] = withCurrentPolicy(reusable, currentAsRow).map(
        (row) => ({ id: String(row.id), search: policySearchText(row), item: row }),
    );

    /**
     * THE ADVANCE IS DRAWN AS THE HIRE PURCHASE AGREEMENT'S DEPENDENT, EXACTLY AS THE WITNESS
     * AGREEMENT IS (owner's ruling, 2026-08-18) — and the lock is `buildBoardSlots`' own derivation,
     * read here rather than re-run, so this block and the manifest's row cannot come apart.
     *
     * SPLIT RATHER THAN ALL-OR-NOTHING, because the lock lifts per instrument: an advance somebody
     * has already signed is past the gate whatever the master instrument is doing, and a contract
     * carrying two would otherwise have the settled one hidden behind the unsettled one's padlock.
     */
    const lockedSlots = slots.filter(advanceLockedFor);
    const openSlots = slots.filter((slot) => !advanceLockedFor(slot));

    /**
     * HOW FAR THE MASTER INSTRUMENT HAS GOT, for the locked panel's middle sentence.
     *
     * FROM THE PAGE'S ONE BOARD, NOT FROM A PROP OF ITS OWN. `agreements.slots` is the whole
     * contract's slot list — this area is handed its own partition of it — and the hire purchase
     * agreement is the one row every satellite on this page is waiting on. Asking the model for it
     * keeps the two areas reading one derivation.
     */
    const hpaSlot = agreements.slots.find((slot) => slot.docType === 'HIRE_PURCHASE') ?? null;
    const hpaSignatures = hpaSlot?.document?.signatures ?? [];
    const hpaSigned = hpaSignatures.filter((signature) => signature.status === 'SIGNED').length;

    return (
        <Blocks>
            <Block id="b-policy" title="Policy on cover">
                {insurance ? (
                    <p className="sd-lede sd-dim">
                        On cover{period ? `, ${period}` : ''}. This junction row is the only thing in
                        this area activation reads.
                    </p>
                ) : (
                    <p className="sd-lede sd-dim">
                        Not on cover. Required to activate — pick a policy this car is already on, or
                        fill in the three values below and save to record a new one. Everything else on
                        this canvas is a record.
                    </p>
                )}

                <Rows>
                    <InlinePickerPopover<PickablePolicy>
                        id="f-policy"
                        label="Policy"
                        rows={policyRows}
                        selectedId={insurance ? String(insurance.id) : null}
                        selectedLabel={currentAsRow ? policyLabel(currentAsRow) : 'Not on cover'}
                        emptyLabel="Not on cover"
                        searchPlaceholder="Search by insured value or period"
                        noun="policy"
                        renderLabel={policyLabel}
                        // THE LABEL IS AN AMOUNT, so it may not shrink below its own text. The
                        // period beside it is the cell built to give way, and at this width both
                        // fit — but `Insured at GH₵…` is one long expiry note away, and
                        // `GH₵90,00…` reads the same as `GH₵90,000.00` at a glance.
                        moneyLabel
                        renderMeta={(row) => policyMeta(row)}
                        rowTitle={(row) => `${policyLabel(row)} · ${policyMeta(row)}`}
                        // 500, NOT 320: `Insured at GH₵90,000.00` is 159px and the period beside
                        // it — `Jan 8, 2026 — Jan 7, 2027 · 18d left` — is 202px, which inside a
                        // 320px surface left the policy's own identity reading `Insured at GH…`.
                        width={500}
                        onSelect={(row) => {
                            const picked = Number(row.id);
                            if (insurance && picked === insurance.id) return;
                            setRefusal(null);
                            if (insurance) {
                                setPendingSwapId(picked);
                                setUnlinkConfirmOpen(true);
                                return;
                            }
                            linkPolicy.mutate(picked);
                        }}
                        loading={reusableLoading}
                        disabled={writesClosed || linkPolicy.isPending || swapPolicy.isPending}
                        // A refusal raised inside a ceremony is read inside it; this row carries
                        // the ones raised from the row itself.
                        error={unlinkConfirmOpen || deleteConfirmOpen ? null : refusal}
                        hint="policies this car is already on"
                    />
                </Rows>

                {(linkPolicy.isPending || swapPolicy.isPending) && (
                    <p className="sd-hint">
                        <Loader2 className="mr-1.5 inline h-3 w-3 animate-spin" />
                        {swapPolicy.isPending ? 'Switching policy…' : 'Linking policy…'}
                    </p>
                )}

                <InlineFieldGroup
                    // THE GROUP'S `discard` IS THIS AREA'S, NOT THE HOOK'S — see `discardPolicy`.
                    // The shell hands `group.discard` to the commit bar and to `Esc`, and neither
                    // may put the fields back while a certificate read is still on its way to them.
                    group={{ ...policy, discard: discardPolicy }}
                    summary={{
                        label: 'Policy details',
                        value: insurance
                            ? [
                                  insurance.valued_at == null
                                      ? 'no insured value'
                                      : `insured at ${formatCurrency(insurance.valued_at)}`,
                                  period === null
                                      ? 'no period recorded'
                                      : `${period}${periodFlag ? ` · ${periodFlag}` : ''}`,
                                  // Only when there is something to say: the tail is absent on a
                                  // certificate that checks out, and on a policy carrying none.
                                  ...(cert.flag ? [cert.flag] : []),
                              ].join(' · ')
                            : 'Nothing recorded — fill these in to create a policy',
                        open: policyOpen,
                        onToggle: () => setPolicyOpen((wasOpen) => !wasOpen),
                    }}
                    footnote="Advisory — no instrument prints any of this. An expired policy still clears the gate, which is exactly why the period is worth being right. Choosing a certificate fills the cover dates from it."
                >
                    <Rows>
                        <QuietField
                            {...policy.field('valuedAt')}
                            id="f-insValue"
                            label="Insured value"
                            inputMode="decimal"
                            placeholder="—"
                            format="GHS"
                            disabled={writesClosed}
                        />
                        {/* ABOVE THE DATES IT FILLS, and that placement is the mitigation: on the
                            happy path the certificate is chosen before either date is typed, so the
                            overwrite the read is entitled to make is rarely an overwrite at all.

                            INSIDE THE GROUP'S BODY, WHICH IS WHERE THE CARET LANDS. `InlineFieldGroup`
                            finds a refused control with `bodyRef.querySelector('[aria-invalid="true"]')`
                            (`InlineFieldGroup.tsx:87`) and the flag rides `InlineUpload`'s visible
                            Choose/Remove button, so the row has to be a child of this group and not a
                            sibling of it. */}
                        {cert.upload}
                        {cert.attestBlock}
                        {/* BOTH DATES POINT AT THE PERIOD WARNING WHILE IT STANDS. The sentence is
                            about the SPAN, so it belongs to neither field on its own and to both of
                            them together: without this it is a paragraph below two inputs that a
                            screen reader arrives at only after passing the controls it is about.
                            `aria-describedby` and not `aria-errormessage` — the period never
                            refuses a save, and `QuietInput` spreads unknown props onto the `input`
                            BEFORE it sets its own `aria-invalid`/`aria-errormessage`, so this
                            cannot displace a server refusal. */}
                        <QuietField
                            {...policy.field('startsAt')}
                            id="f-insStart"
                            label="Cover starts"
                            type="date"
                            disabled={writesClosed}
                            aria-describedby={cert.dateDescribedBy}
                        />
                        <QuietField
                            {...policy.field('expiresAt')}
                            id="f-insExpiry"
                            label="Cover expires"
                            type="date"
                            disabled={writesClosed}
                            aria-describedby={cert.dateDescribedBy}
                        />
                    </Rows>

                    {/* UNDER BOTH DATES, BECAUSE IT IS ABOUT THE SPAN AND NOT ABOUT EITHER END —
                        neither field is wrong on its own. The marked sentence is the canvas's
                        sanctioned weight for a standing fact; `--warning` stays the waiting dot. */}
                    {cert.periodBanner}
                </InlineFieldGroup>

                {/* OUTSIDE THE GROUP, WHICH IS WHERE IT HAS ALWAYS BEEN. `Open the certificate` and
                    `Check again` are about the document ALREADY ON FILE — the group's body is the
                    draft being typed — and the sentences they raise are read beside them rather
                    than inside a block that may be collapsed. */}
                {cert.footer}

                {/* THE TWO DESTRUCTIVE ACTS STAND ON EVERY CONTRACT, chained or not (owner ruling,
                    2026-08-05). On a chain what they reach is the SETUP-ERA junction — the year the
                    contract was activated on — and the unlink cascades over everything recorded
                    against it. Their AlertDialogs are the guard, and they always have been. */}
                {insurance && (
                    <BlockActions>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setPendingSwapId(null);
                                setRefusal(null);
                                setUnlinkConfirmOpen(true);
                            }}
                        >
                            Take this contract off the policy
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setRefusal(null);
                                setDeleteConfirmOpen(true);
                            }}
                        >
                            Delete the policy
                        </Button>
                    </BlockActions>
                )}
            </Block>

            <Block id="b-premium" title="Premium payment">
                <InlineFieldGroup
                    group={premium}
                    footnote="Both links commit together. Recording the premium is what takes its bank transaction out of the Unassigned queue — until it is linked the transaction sits there forever, nobody goes back for it, and activation will not stop you."
                >
                    <Rows>
                        <TransactionPicker
                            id="f-premium"
                            label="Payment to the insurer"
                            type="DEBIT"
                            value={premium.draft.premium}
                            onChange={(value) => premium.set('premium', value)}
                            dirty={premium.field('premium').dirty}
                            error={premium.field('premium').error}
                            disabled={writesClosed || !insurance || premium.saving}
                            emptyLabel={insurance ? 'Not recorded' : 'Link a policy first'}
                            hint="the debit that paid the policy"
                        />
                        <TransactionPicker
                            id="f-discount"
                            label="Discount"
                            type="CREDIT"
                            value={premium.draft.discount}
                            onChange={(value) => premium.set('discount', value)}
                            dirty={premium.field('discount').dirty}
                            error={premium.field('discount').error}
                            disabled={writesClosed || !insurance || premium.saving}
                            emptyLabel={insurance ? 'None recorded' : 'Link a policy first'}
                            hint="the credit the insurer returned"
                        />
                    </Rows>
                </InlineFieldGroup>
                {/*
                 * NO `Premium on file` / `Discount on file` ROWS BELOW THIS. They printed the
                 * amount and the day of the very transactions the two pickers above are already
                 * reading — `GH₵748.00 · Jan 8, 2026` twice, four rows for two facts. THE ROW IS
                 * THE CONTROL on this canvas: what a picker reads at rest IS the record, and a
                 * second read-only copy of it is the duplication the dossier exists to end.
                 */}
            </Block>

            {/*
              * TWO INSTRUMENTS, ONE BLOCK [D-B45]. The fields are the same four either way — the
              * premium, the rate, the weekly recovery and the day it starts — because the SHAPE
              * that decides which instrument this year is on is the deposit against the loan, and
              * the deposit is not written here. What changes is what the block CLAIMS: a year the
              * driver settled in full is not additive financing being recovered from them, and the
              * summary that named it "Advance · 43%" was quoting a rate that accrues nothing.
              */}
            <Block id="b-insloan" title={paidInFull ? 'Premium paid in full' : 'Premium advance'}>
                <p className="sd-lede sd-dim">
                    {paidInFull ? (
                        <>
                            The driver paid this year’s premium in full, so Primelane advanced
                            nothing and no interest arises. The amount is still carried on the
                            contract — it raises what the contract is worth and what has been paid
                            by the same figure, leaving the outstanding balance where it was.
                        </>
                    ) : (
                        <>
                            Primelane paid the premium, and the part it advanced is what it is
                            recovering from the driver — anything the driver paid towards it reduces
                            the advance, and the interest with it. This is additive financing — it
                            never comes out of the selling price.
                        </>
                    )}
                </p>
                {/*
                  * READ-ONLY, AND THE ONLY PLACE THIS CANVAS SPEAKS ABOUT THE DEPOSIT [D-B45].
                  * `insurance_loan_deposits` is written from the policy dossier's Advance canvas
                  * and from the Insurance tab's payment sheet; a control here would be a third door
                  * onto one link row. The line exists because the four fields below cannot show it
                  * and the
                  * block above now depends on it — an admin reading "paid in full" is entitled to
                  * see the payment that says so.
                  */}
                {depositTotal > 0 && (
                    <Rows>
                        <Rec
                            label="Paid by the driver"
                            value={[
                                formatCurrency(depositTotal),
                                depositPaidOn ? formatBusinessDate(depositPaidOn) : null,
                            ]
                                .filter(Boolean)
                                .join(' · ')}
                            sub={
                                paidInFull
                                    ? 'covers the premium in full, so no interest arises'
                                    // [D-B46] — IT REDUCES BOTH, AND THAT IS THE RULING. The
                                    // ledger has always charged interest on `amount − deposits`;
                                    // what moved is the amount advanced, which is now the same
                                    // subtraction. This line used to say the principal survived.
                                    : 'reduces the amount advanced, and the interest with it'
                            }
                        />
                    </Rows>
                )}
                <InlineFieldGroup
                    group={loanGroup}
                    summary={{
                        label: paidInFull ? 'Premium' : 'Advance',
                        value:
                            loan?.amount == null
                                ? 'Not financed'
                                : paidInFull
                                    // NO RATE ON THIS BRANCH, AND THAT IS THE POINT. The deposit
                                    // covers the amount, so the interest base (`amount − deposits`)
                                    // is zero and a stored 43% charges nothing — printing it here
                                    // is the one figure an admin would act on and shouldn't.
                                    ? [formatCurrency(loan.amount), 'paid in full by the driver'].join(' · ')
                                    : [
                                          formatCurrency(loan.amount),
                                          loan.interest_rate == null
                                              ? '0% · interest free'
                                              : `${loan.interest_rate}%`,
                                          // [D-B57] — "no instalment set" reads as an omission, and
                                          // on a contract whose hire purchase agreement leaves no
                                          // room for an advance instalment it is not one: nothing
                                          // separate is added while the vehicle is being paid for,
                                          // and the instalments paid after it recover the advance.
                                          loanInstalment?.amount != null
                                              ? `${formatCurrency(loanInstalment.amount)} weekly`
                                              : hpOnPaper
                                                  ? 'settled at the end of the contract'
                                                  : 'no instalment set',
                                      ].join(' · '),
                        open: loanOpen,
                        onToggle: () => setLoanOpen((wasOpen) => !wasOpen),
                    }}
                    footnote="Financials shows this as a read-only line. The editor lives here, and only here."
                >
                    <Rows>
                        {/*
                          * "PREMIUM" AND NOT "AMOUNT FINANCED" [D-B46]. The column behind this
                          * field is `insurance_loans.amount`, which is what the year of cover
                          * COST — and since the driver's own payment now comes off the advance,
                          * the two figures part company on every part-paid premium. A field
                          * labelled for the advance while holding the premium is the one place an
                          * admin would type the net figure and halve the advance twice over.
                          */}
                        <QuietField
                            {...loanGroup.field('amount')}
                            id="f-loanAmount"
                            label="Premium"
                            inputMode="decimal"
                            placeholder="—"
                            format="GHS · the advance is this less what the driver paid"
                            disabled={writesClosed || !envelope}
                        />
                        <QuietField
                            {...loanGroup.field('rate')}
                            id="f-loanRate"
                            label="Interest rate"
                            inputMode="decimal"
                            placeholder="0"
                            format="% · empty means interest free"
                            disabled={writesClosed || !envelope}
                        />
                        <QuietField
                            {...loanGroup.field('instalment')}
                            id="f-loanInstalment"
                            label="Weekly recovery"
                            inputMode="decimal"
                            placeholder="—"
                            format="GHS per week"
                            disabled={writesClosed || !envelope}
                        />
                        <QuietField
                            {...loanGroup.field('instalmentStart')}
                            id="f-loanInstalmentStart"
                            label="Recovery starts"
                            type="date"
                            disabled={writesClosed || !envelope}
                        />
                    </Rows>
                </InlineFieldGroup>
            </Block>

            {/*
              * [D-B14] — THE BADGE IS A PROPERTY OF THE CONTRACT, NOT OF THE BLOCK.
              *
              * Where the premium is financed this agreement papers a second debt, with its own
              * instalment schedule and its own repayments, and it is required to activate: the
              * eyebrow reads `Required` and every sentence in the block says it is owed. Where
              * there is no loan there is nothing to paper YET — the block keeps its dormant tone
              * and NO aside at all, and it still names the rule that lands the moment a loan is
              * recorded, exactly as the deposit agreement does [D-B15]. The word "optional" never
              * renders here in any state (owner's ruling): a document `sign_contract` refuses to
              * activate without cannot advertise itself as skippable, not even while dormant.
              *
              * THE ONE SENTENCE THAT MAY NOT SURVIVE THE RULING is the old "and it is optional even
              * then". It was true until [D-B14] and it is false now: `sign_contract` reads the
              * insurance-loan arc on every contract that carries a loan, and 170 of the 196
              * contracts carrying one have no sealed loan agreement to read.
              *
              * AND WHAT IS PAPERED HERE HAS TWO NAMES [D-B45]. `INSURANCE_LOAN` publishes an
              * Insurance Premium Advance for a year Primelane financed and an Insurance Premium
              * Payment Record for a year the driver paid in full; the server picks between them at
              * `agreement_create` from the loan's own deposits and instalments. D-B14 is unmoved —
              * both are required to activate — so only the NOUN branches, never the rule.
              */}
            <Block
                id="b-insloanagr"
                title={paidInFull ? 'Insurance premium payment record' : 'Insurance premium advance'}
                aside={loan ? 'Required' : undefined}
            >
                {slots.length === 0 ? (
                    <p className="sd-lede sd-dim">
                        {loan ? (
                            paidInFull ? (
                                <>
                                    Required to activate. The driver paid this year’s premium in
                                    full, so the record of that payment has to be signed, executed
                                    and filed before this contract can start — generate it from the
                                    premium above. It advances nothing and leaves nothing owed; it
                                    is the document the driver asked for.
                                </>
                            ) : (
                                <>
                                    Required to activate. This contract carries a premium advance, so
                                    the agreement over it has to be signed, executed and filed before
                                    this contract can start — generate it from the advance above.
                                </>
                            )
                        ) : (
                            <>
                                Nothing to paper yet. The document is generated against a recorded
                                premium, so it becomes available once one is recorded — and it is
                                required from that moment: an advance where Primelane financed the
                                premium, a payment record where the driver paid it in full, and
                                either way it has to be signed before the contract can be activated.
                            </>
                        )}
                    </p>
                ) : (
                    <>
                        {/*
                          * THE LOCK STANDS WHERE THE ROW WOULD, AND IT STANDS INSTEAD OF IT.
                          *
                          * What an admin used to meet here was the send offered and then refused:
                          * a red "Cannot be sent — 1 merge field is still empty
                          * ({{contract.hire_purchase_reference}})", which named a field nobody can
                          * type and hid the actual sequence behind it. The witness agreement has
                          * always been drawn the other way round — locked, with the condition in
                          * words — and the owner's ruling is that this instrument is the same shape
                          * (2026-08-18). Replacing the card is also what puts the lock AHEAD of the
                          * pre-flight: there is no row left to print a merge box.
                          */}
                        {lockedSlots.length > 0 && (
                            <LockLine title="Locked until the driver signs the hire purchase agreement">
                                {/* BOTH INSTRUMENTS ARE HELD BY THE SAME FIRST MOVE [D-B45/D-B26].
                                    What differs is the reason the master is named: the advance
                                    records a second debt against it, the record states that the
                                    year is settled and that the hire purchase arrangement is
                                    unchanged by it. Either way the reference has to belong to a
                                    bargain somebody has struck. */}
                                {paidInFull ? (
                                    <>
                                        The record is made in connection with the hire purchase
                                        agreement — it names that agreement by number and states that
                                        nothing agreed there changes — so it is not opened before the
                                        master instrument is signed.{' '}
                                    </>
                                ) : (
                                    <>
                                        The advance is made in connection with the hire purchase
                                        agreement — it names that agreement by number and records a
                                        second, separate debt — so it is not opened before the master
                                        instrument is signed.{' '}
                                    </>
                                )}
                                {hpaSlot?.document ? (
                                    <>
                                        <b>
                                            {hpaSigned} of {hpaSignatures.length}
                                        </b>{' '}
                                        signatures are in.
                                    </>
                                ) : (
                                    'The hire purchase agreement has not been generated yet.'
                                )}{' '}
                                The driver’s signature is what opens this, exactly as it opens the
                                witness agreement. Recording a paper original on the hire purchase
                                agreement unlocks it in the same way — the signature is what matters,
                                not the document.
                            </LockLine>
                        )}
                        {openSlots.length > 0 && (
                            <>
                                <p className="sd-lede sd-dim">
                                    {paidInFull
                                        ? 'Required to activate. The premium above has to carry a signed record of the driver’s payment, executed and filed, before this contract can start.'
                                        : 'Required to activate. The advance above has to carry a signed agreement, executed and filed, before this contract can start.'}
                                </p>
                                <AgreementSlotList
                                    slots={openSlots}
                                    agreements={agreements}
                                    onDeepLink={(area) => onGoToArea(area as SetupArea)}
                                />
                            </>
                        )}
                    </>
                )}
            </Block>

            {/* ── the ceremonies, which stay modal ── */}
            <AlertDialog
                open={unlinkConfirmOpen}
                onOpenChange={(open) => {
                    setUnlinkConfirmOpen(open);
                    if (!open) {
                        setPendingSwapId(null);
                        setRefusal(null);
                    }
                }}
            >
                <AlertDialogContent className="sd-tokens">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {pendingSwapId ? 'Change the insurance policy?' : 'Take this contract off cover?'}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            This unlinks the current policy and deletes the advance data recorded against
                            it for this contract. The policy itself is not deleted.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    {/* Verbatim, where the decision was taken. */}
                    {refusal && <p className="sd-f-err">{refusal}</p>}
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                if (pendingSwapId) swapPolicy.mutate(pendingSwapId);
                                else unlinkPolicy.mutate();
                            }}
                            disabled={swapPolicy.isPending || unlinkPolicy.isPending}
                        >
                            {(swapPolicy.isPending || unlinkPolicy.isPending) && (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            Confirm
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog
                open={deleteConfirmOpen}
                onOpenChange={(open) => {
                    setDeleteConfirmOpen(open);
                    if (!open) setRefusal(null);
                }}
            >
                <AlertDialogContent className="sd-tokens">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this policy entirely?</AlertDialogTitle>
                        <AlertDialogDescription>
                            A policy belongs to the car, not to this contract — deleting it removes it
                            from every contract it stands on, along with the premium and advance data
                            recorded against it. Taking this contract off cover is almost always the
                            move instead.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    {/* Verbatim, where the decision was taken. */}
                    {refusal && <p className="sd-f-err">{refusal}</p>}
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => deletePolicy.mutate()}
                            disabled={deletePolicy.isPending}
                        >
                            {deletePolicy.isPending && (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            Delete it
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Blocks>
    );
}
